import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Settings,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  Building,
  CreditCard,
  Bell,
  Mail,
  MessageSquare,
  Cloud,
  BarChart,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchSettingsGrouped,
  bulkUpdateSettings,
  parseSettingValue,
  stringifySettingValue,
  validateSettingValue,
} from "@/api/settings";
import { SETTING_CATEGORIES } from "@/types/settings";
import type { SystemSetting, SettingsGrouped } from "@/types/settings";

interface SettingValue {
  [key: string]: any;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case "app":
      return <Settings className="h-5 w-5" />;
    case "business":
      return <Building className="h-5 w-5" />;
    case "payment":
      return <CreditCard className="h-5 w-5" />;
    case "notification":
      return <Bell className="h-5 w-5" />;
    case "email":
      return <Mail className="h-5 w-5" />;
    case "sms":
      return <MessageSquare className="h-5 w-5" />;
    case "storage":
      return <Cloud className="h-5 w-5" />;
    case "analytics":
      return <BarChart className="h-5 w-5" />;
    case "content":
      return <FileText className="h-5 w-5" />;
    default:
      return <Settings className="h-5 w-5" />;
  }
};

const SystemSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [settingValues, setSettingValues] = useState<SettingValue>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>(
    {}
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  // Fetch settings
  const {
    data: groupedSettings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["systemSettings"],
    queryFn: fetchSettingsGrouped,
  });

  // Initialize form values when data loads
  useEffect(() => {
    if (groupedSettings) {
      const initialValues: SettingValue = {};

      Object.values(groupedSettings)
        .flat()
        .forEach((setting) => {
          initialValues[setting.key] = parseSettingValue(setting);
          initialValues[`${setting.key}_is_public`] = setting.isPublic;
        });

      setSettingValues(initialValues);
    }
  }, [groupedSettings]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async ({
      updates,
      publicUpdates,
    }: {
      updates: Array<{ key: string; value: string }>;
      publicUpdates: Array<{ key: string; isPublic: boolean }>;
    }) => {
      return bulkUpdateSettings(updates, publicUpdates);
    },
    onSuccess: () => {
      toast({
        title: "Settings saved",
        description: "System settings have been updated successfully.",
      });
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ["systemSettings"] });
    },
    onError: (error) => {
      console.error("Error saving settings:", error);
      toast({
        title: "Error saving settings",
        description: "Failed to save some settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Handle input changes
  const handleValueChange = (setting: SystemSetting, value: any) => {
    setSettingValues((prev) => ({
      ...prev,
      [setting.key]: value,
    }));
    setHasChanges(true);

    // Validate the new value
    const errors = validateSettingValue(setting, value);
    setValidationErrors((prev) => ({
      ...prev,
      [setting.key]: errors,
    }));
  };

  // Handle form submission
  const handleSave = async () => {
    if (!groupedSettings) return;

    // Validate all settings before saving
    const allSettings = Object.values(groupedSettings).flat();
    const allErrors: Record<string, string[]> = {};
    let hasValidationErrors = false;

    allSettings.forEach((setting) => {
      const value = settingValues[setting.key];
      const errors = validateSettingValue(setting, value);
      if (errors.length > 0) {
        allErrors[setting.key] = errors;
        hasValidationErrors = true;
      }
    });

    setValidationErrors(allErrors);

    if (hasValidationErrors) {
      toast({
        title: "Validation errors",
        description: "Please fix the validation errors before saving.",
        variant: "destructive",
      });
      return;
    }

    // Prepare value updates
    const updates = allSettings
      .filter((setting) => {
        const currentValue = parseSettingValue(setting);
        const newValue = settingValues[setting.key];
        return currentValue !== newValue;
      })
      .map((setting) => ({
        key: setting.key,
        value: stringifySettingValue(
          settingValues[setting.key],
          setting.dataType
        ),
      }));

    // Prepare public status updates
    const publicUpdates = allSettings
      .filter((setting) => {
        const currentPublic = setting.isPublic;
        const newPublic =
          settingValues[`${setting.key}_is_public`] ?? setting.isPublic;
        return currentPublic !== newPublic;
      })
      .map((setting) => ({
        key: setting.key,
        isPublic: Boolean(
          settingValues[`${setting.key}_is_public`] ?? setting.isPublic
        ),
      }));

    if (updates.length === 0 && publicUpdates.length === 0) {
      toast({
        title: "No changes",
        description: "No settings have been modified.",
      });
      return;
    }

    saveSettingsMutation.mutate({ updates, publicUpdates });
  };

  // Reset to original values
  const handleReset = () => {
    if (groupedSettings) {
      const originalValues: SettingValue = {};
      Object.values(groupedSettings)
        .flat()
        .forEach((setting) => {
          originalValues[setting.key] = parseSettingValue(setting);
          originalValues[`${setting.key}_is_public`] = setting.isPublic;
        });
      setSettingValues(originalValues);
      setHasChanges(false);
      setValidationErrors({});
    }
  };

  // Toggle sensitive field visibility
  const toggleSensitiveVisibility = (key: string) => {
    setShowSensitive((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Render individual setting field
  const renderSettingField = (setting: SystemSetting) => {
    const value = settingValues[setting.key] ?? "";
    const errors = validationErrors[setting.key] || [];
    const hasError = errors.length > 0;

    const fieldId = `setting-${setting.key}`;

    return (
      <div key={setting.key} className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={fieldId} className="text-sm font-medium">
            {setting.key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
            {setting.isRequired && <span className="text-red-500 ml-1">*</span>}
            {setting.isSensitive && (
              <Badge variant="outline" className="ml-2 text-xs">
                Sensitive
              </Badge>
            )}
            {setting.isPublic && (
              <Badge variant="secondary" className="ml-2 text-xs">
                Public API
              </Badge>
            )}
          </Label>
          {setting.isSensitive && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => toggleSensitiveVisibility(setting.key)}
            >
              {showSensitive[setting.key] ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {setting.description && (
          <p className="text-xs text-muted-foreground">{setting.description}</p>
        )}

        {setting.dataType === "boolean" ? (
          <div className="flex items-center space-x-2">
            <Switch
              id={fieldId}
              checked={Boolean(value)}
              onCheckedChange={(checked) => handleValueChange(setting, checked)}
            />
            <Label htmlFor={fieldId} className="text-sm">
              {Boolean(value) ? "Enabled" : "Disabled"}
            </Label>
          </div>
        ) : setting.dataType === "json" ? (
          <Textarea
            id={fieldId}
            value={
              typeof value === "string" ? value : JSON.stringify(value, null, 2)
            }
            onChange={(e) => handleValueChange(setting, e.target.value)}
            placeholder="Enter valid JSON"
            className={`min-h-[100px] font-mono text-sm ${
              hasError ? "border-red-500" : ""
            }`}
          />
        ) : (
          <Input
            id={fieldId}
            type={
              setting.isSensitive && !showSensitive[setting.key]
                ? "password"
                : setting.dataType === "integer" ||
                  setting.dataType === "decimal"
                ? "number"
                : "text"
            }
            value={value}
            onChange={(e) => handleValueChange(setting, e.target.value)}
            placeholder={`Enter ${setting.key.replace(/_/g, " ")}`}
            className={hasError ? "border-red-500" : ""}
            step={setting.dataType === "decimal" ? "0.01" : undefined}
          />
        )}

        {hasError && (
          <div className="space-y-1">
            {errors.map((error, index) => (
              <p
                key={index}
                className="text-xs text-red-500 flex items-center gap-1"
              >
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            ))}
          </div>
        )}

        {/* Public API Toggle */}
        {!setting.isSensitive && (
          <div className="flex items-center space-x-2 mt-2">
            <Switch
              id={`${fieldId}-public`}
              checked={Boolean(
                settingValues[`${setting.key}_is_public`] ?? setting.isPublic
              )}
              onCheckedChange={(checked) => {
                setSettingValues((prev) => ({
                  ...prev,
                  [`${setting.key}_is_public`]: checked,
                }));
                setHasChanges(true);
              }}
            />
            <Label
              htmlFor={`${fieldId}-public`}
              className="text-xs text-muted-foreground"
            >
              Available via Public API (for mobile/frontend apps)
            </Label>
          </div>
        )}
      </div>
    );
  };

  // Render settings category
  const renderSettingsCategory = (
    category: string,
    settings: SystemSetting[]
  ) => {
    const categoryMeta = SETTING_CATEGORIES.find((c) => c.key === category);
    const categoryTitle =
      categoryMeta?.title ||
      category.charAt(0).toUpperCase() + category.slice(1);
    const categoryDescription =
      categoryMeta?.description || `${categoryTitle} configuration settings`;

    return (
      <Card key={category}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getCategoryIcon(category)}
            {categoryTitle}
          </CardTitle>
          <CardDescription>{categoryDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings.map(renderSettingField)}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout title="System Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="System Settings">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load system settings. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  if (!groupedSettings) {
    return (
      <AdminLayout title="System Settings">
        <div className="text-center py-8">
          <p className="text-muted-foreground">No settings found.</p>
        </div>
      </AdminLayout>
    );
  }

  const sortedCategories = SETTING_CATEGORIES.filter(
    (cat) => groupedSettings[cat.key]
  ).sort((a, b) => a.order - b.order);

  return (
    <AdminLayout title="System Settings">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">System Settings</h1>
            <p className="text-muted-foreground">
              Configure system-wide settings and integrations
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saveSettingsMutation.isPending}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Status indicator */}
        {hasChanges && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You have unsaved changes. Don't forget to save your settings.
            </AlertDescription>
          </Alert>
        )}

        {/* Settings organized in tabs */}
        <Tabs defaultValue={sortedCategories[0]?.key} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5">
            {sortedCategories.slice(0, 5).map((category) => (
              <TabsTrigger
                key={category.key}
                value={category.key}
                className="flex items-center gap-2"
              >
                {getCategoryIcon(category.key)}
                <span className="hidden sm:inline">{category.title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {sortedCategories.map((category) => (
            <TabsContent key={category.key} value={category.key}>
              {renderSettingsCategory(
                category.key,
                groupedSettings[category.key]
              )}
            </TabsContent>
          ))}

          {/* Additional categories in a separate section if there are more than 5 */}
          {sortedCategories.length > 5 && (
            <div className="space-y-4">
              <Separator />
              <h2 className="text-lg font-semibold">Additional Settings</h2>
              <div className="grid gap-6">
                {sortedCategories
                  .slice(5)
                  .map((category) =>
                    renderSettingsCategory(
                      category.key,
                      groupedSettings[category.key]
                    )
                  )}
              </div>
            </div>
          )}
        </Tabs>

        {/* Success message */}
        {saveSettingsMutation.isSuccess && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Settings have been saved successfully.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
